// ════════════════════════════════════════════════════════════════════════
//  Der Stand eines Kindes – je Lehrgang und über alle Lehrgänge
//
//  Reine Rechnung, ohne React. Was ein Kind geübt hat, hängt an
//  Stufenschlüsseln und weiß nichts von Lehrgängen; die Zuordnung entsteht
//  erst hier, gegen das Bundle der Fassung, die diese Klasse übt.
//
//  Gerechnet wird EINMAL: das Fortschrittsband der Übersicht, die Lektionsliste
//  des Kindblicks und die Urkunde lesen alle aus `courseProgress`. Vorher zählte
//  `summarize` in features/certificates seine Stufen und Sterne selbst – zwei
//  Stellen, die dasselbe rechnen, rechnen es irgendwann verschieden.
// ════════════════════════════════════════════════════════════════════════

import { lessonsInOrder, type Bundle } from "@otto/lehrgang";
import type { ClassCourse } from "../../courses/lib/data.js";
import type { StudentRow } from "./data.js";
import { starsForLesson, type LessonMark } from "./lessonStars.js";
import { speedOf, type Speed } from "./speed.js";

/**
 * Eine Lektion im Fortschrittsband.
 *
 * `mark === null` heißt „nie begonnen“ – nicht „null Sterne“. Der Unterschied
 * ist die halbe Aussage des Bandes: eine graue Zelle ist keine schlechte
 * Leistung, sondern gar keine.
 */
export type BandCell = {
  readonly key: string;
  readonly number: number;
  readonly title: string;
  readonly subtitle: string;
  /** Stufen dieser Lektion: geübte und vorhandene. Der Kindblick zeigt sie als
   *  „2 von 3 Stufen“ – gezählt wird hier, damit er dafür nicht selbst durch das
   *  Bundle laufen muss. */
  readonly stagesDone: number;
  readonly stagesTotal: number;
  readonly mark: LessonMark | null;
};

export type CourseProgress = {
  /** Alle Lektionen des Lehrgangs in ihrer Reihenfolge – auch die nie begonnenen. */
  readonly lessons: readonly BandCell[];
  readonly stagesDone: number;
  readonly stagesTotal: number;
  /** Summe der Stufensterne. Dieselbe Zahl, die auf der Urkunde steht. */
  readonly stars: number;
  readonly speed: Speed | null;
};

/**
 * Was dieses Kind in DIESEM Lehrgang geübt hat.
 *
 * Die Lektionsliste ist immer vollständig, auch wenn das Kind den Lehrgang nie
 * geöffnet hat: das leere Band zeigt dann mit, wie lang der Lehrgang ist, und
 * die Zeilen der Klasse bleiben untereinander vergleichbar.
 */
export function courseProgress(row: StudentRow, bundle: Bundle): CourseProgress {
  const lessons = lessonsInOrder(bundle).map((lesson) => ({
    key: lesson.key,
    number: lesson.number,
    title: lesson.title,
    subtitle: lesson.subtitle,
    stagesDone: lesson.stages.filter((stage) => row.stages[stage.key]).length,
    stagesTotal: lesson.stages.length,
    mark: starsForLesson(lesson, row, bundle.starThresholds),
  }));

  let stagesDone = 0;
  let stagesTotal = 0;
  let stars = 0;
  for (const lesson of bundle.lessons) {
    for (const stage of lesson.stages) {
      stagesTotal++;
      const mark = row.stages[stage.key];
      if (!mark) continue;
      stagesDone++;
      stars += mark.stars;
    }
  }

  return { lessons, stagesDone, stagesTotal, stars, speed: speedOf(row, bundle) };
}

/** Ein Lehrgang der Klasse in der Zeile eines Kindes. */
export type CourseCell = {
  readonly curriculumId: string;
  readonly title: string;
  readonly version: number;
  /** Ohne Test gibt es keine Tempospalten – nicht leere. */
  readonly hasAssessment: boolean;
  readonly progress: CourseProgress;
};

export type OverviewRow = {
  readonly code: string;
  readonly lastAt: string | null;
  readonly courses: readonly CourseCell[];
};

/**
 * Eine Zeile je Kind, eine Spaltengruppe je Lehrgang.
 *
 * Die Spalten SIND die Lehrgänge der Klasse – jedes Kind hat deshalb zu jedem
 * eine Zelle, auch zu einem, den es nie geöffnet hat. Ein „–“ gäbe es nur für
 * einen Lehrgang, den die Klasse gar nicht führt, und der hat hier keine Spalte.
 *
 * Lehrgänge mit unlesbarer Fassung fehlen: für die steht ein Fehlerhinweis über
 * der Tabelle, und eine Spalte ohne Lektionen wäre eine zweite, stillere Meldung
 * desselben Problems.
 *
 * Die Reihenfolge der Lehrgänge ist die der Klasse, die der Kinder der Code.
 * Nicht der Fortschritt: eine Tabelle, die sich nach Ergebnis ordnet, ist eine
 * Bestenliste.
 */
export function overviewRows(
  rows: readonly StudentRow[],
  courses: readonly ClassCourse[],
): OverviewRow[] {
  const readable = courses.filter(
    (course): course is ClassCourse & { bundle: Bundle } => course.bundle !== null,
  );

  return rows.map((row) => ({
    code: row.code,
    lastAt: row.lastAt,
    courses: readable.map((course) => ({
      curriculumId: course.curriculumId,
      title: course.title,
      version: course.version,
      hasAssessment: course.bundle.assessment !== undefined,
      progress: courseProgress(row, course.bundle),
    })),
  }));
}
